import React, { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Resizable } from 're-resizable';
import { useLocation, useNavigate } from 'react-router-dom';

import { Drawer, Modal } from 'antd';

import {
  isTicketOpen,
  setDrawerWidth
} from '../../../actions';

import ConfirmationModal from '../molecules/modals/ConfirmationModal';
import TaskNavSection from '../molecules/task-side-panel/TaskNavSection';
import TaskDetailSection from '../molecules/task-side-panel/TaskDetailSection';

import TaskPanelSkeleton from './skeletons/TaskPanelSkeleton';
import { getOpenedTaskTitleIdentifier } from '../../../redux/tasks/taskEntities/selectors';
import { clearOpenedTaskId, deleteTaskAction, openTaskAction } from '../../../redux/tasks/actions';
import { getOpenedTaskId } from '../../../redux/general/selectors';
import { getOpenedTaskProjectAndSectionLabelList } from '../../../redux/tasks/tasksToProjectsRelation/selectors';

import '../../../assets/style/taskDrawer.css';

const TaskSidePanel = ({
  customClass,
  drawerWidth,
  projectId,
  taskId,
  titleIdentifier,
  overlay,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isModalDefaultView = useSelector(state => state.toggleReducer.showTaskAsModal);
  const isMobile = useSelector(state => state.toggleReducer.isMobile);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isModalView, setIsModalView] = useState(isModalDefaultView);
  const [showParentTaskSearch, setShowParentTaskSearch] = useState(false);
  const [subTask,setSubTask] = useState({});

  const selectedTaskParam = new URLSearchParams(location.search).get('selectedTask');

  const deleteHandler = async () => {
    await dispatch(deleteTaskAction(taskId));
    await dispatch(isTicketOpen(false));
    const params = new URLSearchParams(location.search);
    params.delete('selectedTask'); // Remove the specified parameter
    const newUrl = `${location.pathname}?${params.toString()}`;
    navigate(newUrl, { replace: true });
  };

  useEffect(() => {
    return () => {
      dispatch(clearOpenedTaskId());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTaskParam && titleIdentifier !== selectedTaskParam) {
      dispatch(openTaskAction(false, selectedTaskParam));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskParam])

  const [selectedTab, setSelectedTab] = useState('1');

  const taskComponent = () => {
    return (
      <>
        <TaskNavSection
          setDeleteModalOpen={setDeleteModalOpen}
          projectId={projectId}
          setIsModalView={setIsModalView}
          isModal={isModalView}
          setSelectedTab={setSelectedTab}
          setShowParentTaskSearch={setShowParentTaskSearch}
          setSubTask={setSubTask}
        />
       <TaskDetailSection
          projectId={projectId}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          isModal={isModalView}
          showParentTaskSearch={showParentTaskSearch}
          setShowParentTaskSearch={setShowParentTaskSearch}
          subTask={subTask}/>
      </>
    )
  }

  return (
    <>
      <ConfirmationModal
        title='Delete Task?'
        description='Are you sure you want to delete this task?'
        isModalOpen={isDeleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onYesClick={deleteHandler}
      />

      {!isModalView && (
        <>
          {isMobile ? (
            <Drawer
              placement="right"
              open={true}
              onClose={() => {
                dispatch(isTicketOpen(false));
                const params = new URLSearchParams(location.search);
                params.delete('selectedTask');
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
              }}
              width="100vw"
              mask={true}
              styles={{ body: { padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }, header: { display: 'none' } }}
              style={{ zIndex: 1000 }}
            >
              <div className='open-ticket' style={{ height: '100%', minWidth: 'unset', borderLeft: 'none' }}>
                {taskId ? taskComponent() : <TaskPanelSkeleton />}
              </div>
            </Drawer>
          ) : overlay ? (
            <Drawer
              placement="right"
              open={true}
              onClose={() => {
                dispatch(isTicketOpen(false));
                const params = new URLSearchParams(location.search);
                params.delete('selectedTask');
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
              }}
              width={drawerWidth || 510}
              mask={false}
              styles={{
                body: { padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
                header: { display: 'none' },
                wrapper: { top: 52, boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' },
              }}
              style={{ zIndex: 100 }}
            >
              <div className='open-ticket' style={{ height: '100%', minWidth: 'unset', borderLeft: 'none' }}>
                {taskId ? taskComponent() : <TaskPanelSkeleton />}
              </div>
            </Drawer>
          ) : (
            <div style={{ position: 'absolute', right: 0, top: 0, height: '100%', zIndex: 100 }}>
              <Resizable
                className={customClass}
                enable={{
                  top: false,
                  right: false,
                  bottom: false,
                  left: true,
                  topRight: false,
                  bottomRight: false,
                  bottomLeft: false,
                  topLeft: false,
                }}
                defaultSize={{
                  width: drawerWidth,
                  height: '100%',
                }}
                maxWidth='750px'
                minWidth='510px'
                onResizeStart={(e, direction, ref, delta) => {
                  ref.classList.add('border-left-blue');
                }}
                onResizeStop={(e, direction, ref, delta) => {
                  ref.classList.remove('border-left-blue');
                  dispatch(setDrawerWidth(ref.offsetWidth));
                }}
              >
                <div className='open-ticket'>
                  {taskId ? taskComponent() : <TaskPanelSkeleton />}
                </div>
              </Resizable>
            </div>
          )}
        </>
      )}

      {taskId && isModalView && (
        <Modal
          centered
          open={isModalView}
          footer={null}
          width="min(1340px, 95vw)"
          zIndex={998}
          bodyStyle={{ height: '100%', margin: 0, padding: 0}}
          onCancel={async () => {
            const params = new URLSearchParams(location.search);
            params.delete('selectedTask');
            const newUrl = `${location.pathname}?${params.toString()}`;
            navigate(newUrl);

            await dispatch(isTicketOpen(false));
          }}
          className='taskDetailsModal'
          closeIcon={<></>}
        >
          {taskComponent()}
        </Modal>
      )}
    </>
  );
};
function mapStateToProps(state) {
  return {
    taskId: getOpenedTaskId(state),
    titleIdentifier: getOpenedTaskTitleIdentifier(state),
    drawerWidth: state.toggleReducer.drawerWidth,
    projectId: Object.keys(getOpenedTaskProjectAndSectionLabelList(state) || {})[0] || state.projectReducer.getProjects?._id,
  };
}

export default connect(mapStateToProps)(TaskSidePanel);
